import {
  INBOX_GROUPS,
  PINNED_INBOX_GROUPS,
  TRAILING_INBOX_GROUPS,
  SLA_WARNING_TOKENS,
  SLA_BREACH_TOKENS,
  EVIDENCE_TOKENS,
  APPROVAL_TOKENS,
  MESSAGE_TOKENS,
  MENTION_TOKENS,
} from './company_inbox_constants';
import {
  first_present,
  normalize_key,
  normalize_text,
} from '@/views/x-verdict_views/CompanyAdmin/CompanyCases/company_cases_common';
import { status_dot_class } from '@/views/x-verdict_views/CaseOwner/AllCases/case_state';

function includes_any(value = '', tokens = []) {
  return tokens.some((token) => value.includes(token));
}

function lower_text(value = '') {
  return normalize_text(value).toLowerCase();
}

function case_search_text(case_item = {}) {
  return lower_text([
    case_item?.status,
    case_item?.title,
    case_item?.subtitle,
    case_item?.domain,
    case_item?.sla,
    case_item?.sla_class,
    case_item?.risk_label,
    case_item?.sla_runtime?.status,
    case_item?.sla_runtime?.status_label,
    case_item?.sla_runtime?.badge_label,
    case_item?.raw_record?.due_status,
  ].filter(Boolean).join(' '));
}

function sla_search_text(case_item = {}) {
  return lower_text([
    case_item?.sla,
    case_item?.sla_class,
    case_item?.sla_runtime?.status,
    case_item?.sla_runtime?.status_label,
    case_item?.sla_runtime?.badge_label,
    case_item?.raw_record?.due_status,
  ].filter(Boolean).join(' '));
}

function is_sla_attention_case(case_item = {}) {
  const value = sla_search_text(case_item);
  return includes_any(value, SLA_WARNING_TOKENS) || includes_any(value, SLA_BREACH_TOKENS);
}

function is_sla_breached_case(case_item = {}) {
  return includes_any(sla_search_text(case_item), SLA_BREACH_TOKENS);
}

function is_high_risk_case(case_item = {}) {
  const risk_label = lower_text(case_item?.risk_label);
  const risk_score = Number(case_item?.risk_score);
  return risk_label.includes('high') || (Number.isFinite(risk_score) && risk_score >= 70);
}

function is_sla_risk_case(case_item = {}) {
  return is_sla_attention_case(case_item) || is_high_risk_case(case_item);
}

function leading_dot_class(case_item = {}) {
  if (is_sla_breached_case(case_item) || is_high_risk_case(case_item)) return 'bg-danger';
  if (is_sla_attention_case(case_item)) return 'bg-warning';
  return 'bg-success';
}

function inner_dot_class(case_item = {}) {
  if (is_sla_breached_case(case_item)) return 'bg-danger';
  if (is_sla_attention_case(case_item)) return 'bg-warning';
  return case_item?.status_dot_class || 'bg-success';
}

function case_time(case_item = {}) {
  const timestamp = Number(case_item?.sort_time || 0);
  if (!timestamp) return '';

  const diff_minutes = Math.max(0, Math.floor((Date.now() - timestamp) / 60000));
  if (diff_minutes < 1) return 'NOW';
  if (diff_minutes < 60) return `${diff_minutes}M AGO`;

  const diff_hours = Math.floor(diff_minutes / 60);
  if (diff_hours < 24) return `${diff_hours}H AGO`;

  const diff_days = Math.floor(diff_hours / 24);
  return diff_days === 1 ? 'YESTERDAY' : `${diff_days}D AGO`;
}

function inbox_title(case_item = {}) {
  const case_code = normalize_text(case_item?.case_code) || 'Case';
  const status = normalize_text(case_item?.status);

  if (is_sla_risk_case(case_item)) {
    return `${is_sla_breached_case(case_item) ? 'SLA breached' : 'SLA attention'} - ${case_code}`;
  }

  return status ? `${status} - ${case_code}` : `Case update - ${case_code}`;
}

function inbox_message(case_item = {}) {
  const parts = [
    normalize_text(case_item?.title),
    normalize_text(case_item?.domain),
    normalize_text(case_item?.status),
    normalize_text(case_item?.sla),
    normalize_text(case_item?.risk_label) && `Risk: ${case_item.risk_label}`,
  ].filter(Boolean);

  return parts.join(' - ') || normalize_text(case_item?.subtitle) || 'Open this case to continue work.';
}

function action_label(case_item = {}) {
  const value = case_search_text(case_item);
  if (is_sla_risk_case(case_item)) {
    return is_sla_breached_case(case_item) ? 'Decide now' : 'Review SLA';
  }
  if (includes_any(value, EVIDENCE_TOKENS)) return 'Review evidence';
  if (includes_any(value, MESSAGE_TOKENS)) return 'Open thread';
  if (includes_any(value, APPROVAL_TOKENS)) return 'Review approval';
  return 'Open case';
}

function inbox_actions(case_item = {}) {
  if (is_sla_risk_case(case_item)) {
    return [
      { label: action_label(case_item), primary: true, opens_case: true },
      { label: 'Reassign', primary: false, opens_case: true },
      { label: 'Snooze 4h', primary: false, opens_case: true },
    ];
  }

  return [{ label: action_label(case_item), primary: true, opens_case: true }];
}

function inbox_category(case_item = {}) {
  return normalize_key(case_item?.status) || 'uncategorised';
}

function raw_note_body(note = {}) {
  return normalize_text(first_present([
    note?.body,
    note?.journal_description,
    note?.description,
    note?.note,
  ]));
}

function note_reply_meta(body = '') {
  const value = normalize_text(body);
  const match = /^\[ReplyTo:([^\]]+)]\s*/.exec(value);

  if (match) {
    return {
      is_reply: true,
      reply_to: normalize_text(match[1]),
      display_body: value.slice(match[0].length),
    };
  }

  return {
    is_reply: false,
    reply_to: '',
    display_body: value,
  };
}

function note_body(note = {}) {
  return note_reply_meta(raw_note_body(note)).display_body;
}

function note_author(note = {}) {
  const full_name = [note?.user_first_name, note?.user_last_name]
    .map(normalize_text)
    .filter(Boolean)
    .join(' ');

  return normalize_text(first_present([
    note?.author,
    note?.user_full_name,
    full_name,
    note?.created_by_name,
    note?.user_email,
    'Unknown user',
  ]));
}

function note_user_id(note = {}) {
  const value = Number(first_present([
    note?.user_id,
    note?.created_by_id,
    note?.created_by?.id,
    note?.user?.id,
  ]));

  return Number.isFinite(value) && value > 0 ? value : null;
}

function note_time(note = {}) {
  const value = first_present([
    note?.date_created,
    note?.created_at,
    note?.journal_date,
    note?.timestamp,
  ]);
  const date = value ? new Date(value) : null;

  if (!date || Number.isNaN(date.getTime())) {
    return '';
  }

  return date.toLocaleDateString('en-ZA', {
    day: '2-digit',
    month: 'short',
  }).toUpperCase();
}

function escape_regexp(value = '') {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function token_pattern(value = '') {
  return escape_regexp(normalize_text(value)).replace(/\s+/g, '[\\s._-]+');
}

function mention_matches_token(body = '', token = '') {
  const cleaned_token = normalize_text(token);
  if (!cleaned_token) {
    return false;
  }

  const pattern = new RegExp(`(^|\\s)@${token_pattern(cleaned_token)}(?=\\s|$|[.,;:!?])`, 'i');
  return pattern.test(body);
}

export function note_mentions_current_user(note = {}, tokens = []) {
  const body = note_body(note);
  return tokens.some((token) => mention_matches_token(body, token));
}

export function note_authored_by_current_user(note = {}, current_user_id = null, tokens = []) {
  const author_user_id = note_user_id(note);
  if (current_user_id && author_user_id && Number(current_user_id) === Number(author_user_id)) {
    return true;
  }

  const author = lower_text(note_author(note));
  return tokens
    .map((token) => lower_text(String(token).replace(/[._-]+/g, ' ')))
    .filter(Boolean)
    .some((token) => author === token);
}

export function map_note_to_mention_item(case_item = {}, note = {}, index = 0) {
  const case_code = normalize_text(case_item?.case_code) || 'Case';
  const reply_meta = note_reply_meta(raw_note_body(note));
  const body = note_body(note);
  const author = note_author(note);

  return {
    id: `mention-${case_item?.application_id || case_code}-${note?.id || index}`,
    category: 'mentions',
    title: `${reply_meta.is_reply ? 'Reply' : 'Mention'} - ${case_code}`,
    message: `${author}: ${body}`,
    time: note_time(note),
    leading_dot_class: 'bg-success',
    inner_dot_class: 'bg-success',
    reply_to_name: author,
    actions: [
      { label: 'Open note', primary: true, opens_case: true },
      { label: 'Reply', primary: false, opens_reply: true },
    ],
    case_item: {
      ...case_item,
      active_tab: 'notes',
    },
  };
}

export function map_case_to_inbox_item(case_item = {}, index = 0) {
  const category = inbox_category(case_item);

  return {
    id: case_item?.application_id || case_item?.entity_profile_id || case_item?.case_code || `case-${index + 1}`,
    category,
    title: inbox_title(case_item),
    message: inbox_message(case_item),
    time: case_time(case_item),
    leading_dot_class: leading_dot_class(case_item),
    inner_dot_class: inner_dot_class(case_item),
    actions: inbox_actions(case_item),
    case_item,
  };
}

export function inbox_item_matches_group(item = {}, group_key = 'all') {
  if (group_key === 'all') return true;
  if (group_key === 'sla-risk') return is_sla_risk_case(item.case_item);
  if (group_key === 'messages') return includes_any(case_search_text(item.case_item), MESSAGE_TOKENS);
  if (group_key === 'mentions') return includes_any(case_search_text(item.case_item), MENTION_TOKENS);
  return item.category === group_key;
}

export function group_count(items = [], group_key = 'all') {
  return items.filter((item) => inbox_item_matches_group(item, group_key)).length;
}

function unique_by(items = [], key_builder = (item) => item) {
  const seen = new Set();
  return items.filter((item) => {
    const key = key_builder(item);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function status_order(case_item = {}, fallback = 0) {
  const order = Number(first_present([
    case_item?.raw_record?.current_status?.order,
    case_item?.raw_record?.current_status?.order_number,
    case_item?.raw_record?.workflow_status?.order,
    case_item?.raw_record?.workflow_status?.order_number,
    case_item?.raw_record?.status?.order,
    case_item?.raw_record?.status?.order_number,
    fallback,
  ]));
  return Number.isFinite(order) ? order : fallback;
}

export function workflow_status_groups(items = []) {
  const status_groups = unique_by(
    items
      .map((item, index) => {
        const label = normalize_text(item?.case_item?.status);
        const key = normalize_key(label);
        return key
          ? { label, key, dot_class: status_dot_class(label), order: status_order(item?.case_item, index) }
          : null;
      })
      .filter(Boolean),
    (group) => group.key
  ).sort((left, right) => {
    const order_diff = Number(left.order || 0) - Number(right.order || 0);
    return order_diff || String(left.label || '').localeCompare(String(right.label || ''));
  });

  return [...INBOX_GROUPS, ...PINNED_INBOX_GROUPS, ...status_groups, ...TRAILING_INBOX_GROUPS];
}

export function due_at_date(case_item = {}) {
  const value = first_present([
    case_item?.sla_runtime?.due_at,
    case_item?.raw_record?.sla_runtime?.due_at,
    case_item?.raw_record?.due_at,
  ]);
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime()) ? date : null;
}

export function is_today(date) {
  const now = new Date();
  return date
    && date.getFullYear() === now.getFullYear()
    && date.getMonth() === now.getMonth()
    && date.getDate() === now.getDate();
}

export function is_this_week(date) {
  if (!date) return false;
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const end = new Date(start);
  end.setDate(start.getDate() + 7);
  return date >= start && date < end;
}

export function case_detail_query(case_item = {}) {
  return Object.fromEntries([
    ['application_id', case_item.application_id],
    ['entity_profile_id', case_item.entity_profile_id],
    ['entity_type_id', case_item.entity_type_id],
    ['risk_assessment_id', case_item.risk_assessment_id],
    ['tab', case_item.active_tab]
  ].filter((entry) => Boolean(entry[1])));
}
